// src/pages/RequestDemoPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  Fade,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import routes from '../config/routes';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { HeaderLogo } from '../components/virtualpat-theme/VirtualPATLogo';
import { apiUtils } from '../utils/apiUtils';

const facilityTypes = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'asc', label: 'Ambulatory Surgery Center (ASC)' },
  { value: 'health_system', label: 'Health System (Multiple Facilities)' },
  { value: 'clinic', label: 'Surgical Clinic' },
];

const facilitySizes = [
  { value: 'small', label: '1-50 surgeries/month' },
  { value: 'medium', label: '51-200 surgeries/month' },
  { value: 'large', label: '201-500 surgeries/month' },
  { value: 'enterprise', label: '500+ surgeries/month' },
];

const timeframes = [
  { value: 'immediate', label: 'Immediate (within 2 weeks)' },
  { value: '1month', label: 'Within 1 month' },
  { value: '3months', label: 'Within 3 months' },
  { value: '6months', label: 'Within 6 months' },
  { value: 'evaluating', label: 'Just evaluating' },
];

const interests = [
  'Pre-operative Assessment',
  'AI-Driven Insights',
  'Protocol Management',
  'EMR Integration',
  'Multi-facility Support',
  'Compliance & Reporting',
  'Staff Training',
  'Custom Workflows',
];

export default function RequestDemoPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const [formData, setFormData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    
    // Facility Information
    facilityName: '',
    facilityType: '',
    facilitySize: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Requirements
    timeframe: '',
    interests: [],
    currentEMR: '',
    message: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const steps = ['Contact Information', 'Facility Details', 'Requirements'];

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: '',
      });
    }
  };

  const handleInterestToggle = (interest) => {
    const currentInterests = formData.interests;
    const newInterests = currentInterests.includes(interest)
      ? currentInterests.filter(i => i !== interest)
      : [...currentInterests, interest];
    
    setFormData({
      ...formData,
      interests: newInterests,
    });
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0: // Contact Information
        if (!formData.firstName) errors.firstName = 'First name is required';
        if (!formData.lastName) errors.lastName = 'Last name is required';
        if (!formData.email) errors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email address';
        if (!formData.phone) errors.phone = 'Phone number is required';
        if (!formData.title) errors.title = 'Title is required';
        break;
        
      case 1: // Facility Details
        if (!formData.facilityName) errors.facilityName = 'Facility name is required';
        if (!formData.facilityType) errors.facilityType = 'Facility type is required';
        if (!formData.facilitySize) errors.facilitySize = 'Facility size is required';
        if (!formData.city) errors.city = 'City is required';
        if (!formData.state) errors.state = 'State is required';
        break;
        
      case 2: // Requirements
        if (!formData.timeframe) errors.timeframe = 'Timeframe is required';
        if (formData.interests.length === 0) errors.interests = 'Please select at least one area of interest';
        break;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Submit to API
      await apiUtils.post('/api/demo-requests', {
        ...formData,
        submittedAt: new Date().toISOString(),
      });
      
      setShowSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          title: '',
          facilityName: '',
          facilityType: '',
          facilitySize: '',
          city: '',
          state: '',
          zipCode: '',
          timeframe: '',
          interests: [],
          currentEMR: '',
          message: '',
        });
        setActiveStep(0);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to submit demo request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: 18, sm: 20 } }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: 18, sm: 20 } }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange('phone')}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: 18, sm: 20 } }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title/Role"
                value={formData.title}
                onChange={handleChange('title')}
                error={!!formErrors.title}
                helperText={formErrors.title}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            </Grid>
          </Grid>
        );
        
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Facility Name"
                value={formData.facilityName}
                onChange={handleChange('facilityName')}
                error={!!formErrors.facilityName}
                helperText={formErrors.facilityName}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
                InputProps={{
                  startAdornment: <BusinessIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: 18, sm: 20 } }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.facilityType} variant="outlined">
                <InputLabel>Facility Type</InputLabel>
                <Select
                  value={formData.facilityType}
                  onChange={handleChange('facilityType')}
                  label="Facility Type"
                  startAdornment={<LocalHospitalIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {facilityTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.facilityType && (
                  <FormHelperText>{formErrors.facilityType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.facilitySize} variant="outlined">
                <InputLabel>Facility Size</InputLabel>
                <Select
                  value={formData.facilitySize}
                  onChange={handleChange('facilitySize')}
                  label="Facility Size"
                  startAdornment={<GroupsIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {facilitySizes.map((size) => (
                    <MenuItem key={size.value} value={size.value}>
                      {size.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.facilitySize && (
                  <FormHelperText>{formErrors.facilitySize}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleChange('city')}
                error={!!formErrors.city}
                helperText={formErrors.city}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
                InputProps={{
                  startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: 18, sm: 20 } }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={handleChange('state')}
                error={!!formErrors.state}
                helperText={formErrors.state}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Zip Code"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            </Grid>
          </Grid>
        );
        
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.timeframe} variant="outlined">
                <InputLabel>Implementation Timeframe</InputLabel>
                <Select
                  value={formData.timeframe}
                  onChange={handleChange('timeframe')}
                  label="Implementation Timeframe"
                  startAdornment={<CalendarTodayIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {timeframes.map((time) => (
                    <MenuItem key={time.value} value={time.value}>
                      {time.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.timeframe && (
                  <FormHelperText>{formErrors.timeframe}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current EMR System (Optional)"
                value={formData.currentEMR}
                onChange={handleChange('currentEMR')}
                placeholder="e.g., Epic, Cerner, Athena..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1" fontWeight={600} gutterBottom color="#1a1a1a" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Areas of Interest
              </Typography>
              {formErrors.interests && (
                <Typography variant="body2" color="error" gutterBottom sx={{ mb: 2 }}>
                  {formErrors.interests}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {interests.map((interest) => (
                  <Chip
                    key={interest}
                    label={interest}
                    onClick={() => handleInterestToggle(interest)}
                    variant={formData.interests.includes(interest) ? 'filled' : 'outlined'}
                    color={formData.interests.includes(interest) ? 'primary' : 'default'}
                    icon={formData.interests.includes(interest) ? <CheckCircleIcon /> : undefined}
                    sx={{
                      borderWidth: formData.interests.includes(interest) ? 0 : 1.5,
                      fontWeight: formData.interests.includes(interest) ? 600 : 400,
                      '&:hover': {
                        bgcolor: formData.interests.includes(interest) 
                          ? 'primary.dark' 
                          : alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Information (Optional)"
                value={formData.message}
                onChange={handleChange('message')}
                placeholder="Tell us more about your specific needs or questions..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1, sm: 1.5 },
                  },
                }}
              />
            </Grid>
          </Grid>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Professional Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.3s ease',
          borderBottom: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          boxShadow: scrolled ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 } }}>
          <Box>
            <HeaderLogo 
              onClick={() => navigate('/')}
              sx={{ 
                cursor: 'pointer',
                '& svg': {
                  height: { xs: 28, sm: 32 },
                }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
            <Button
              sx={{ 
                color: 'text.primary',
                fontWeight: 500,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 1, sm: 2 },
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
              onClick={() => navigate(routes.login())}
            >
              Sign In
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                borderColor: '#1a1a1a',
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 0.5, sm: 1 },
                borderWidth: 2,
                '&:hover': {
                  borderColor: '#1a1a1a',
                  bgcolor: '#1a1a1a',
                  color: 'white',
                  borderWidth: 2,
                },
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Minimal Hero Section */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          pt: { xs: 9, md: 10 }, // Further reduced padding
          pb: 1,
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 800,
              color: '#1a1a1a',
              mb: 1,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              lineHeight: 1.2,
            }}
          >
            Schedule Your Demo
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: '#4a5568',
              mb: 2,
              fontWeight: 400,
              fontSize: { xs: '0.875rem', md: '1rem' },
            }}
          >
            See VirtualPAT in action. Takes less than 2 minutes.
          </Typography>
        </Container>
      </Box>
      
      {/* Compact Benefits Strip */}
      <Container maxWidth="md" sx={{ mb: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            p: { xs: 1.5, sm: 2 },
            bgcolor: 'rgba(0, 102, 255, 0.04)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', minWidth: { xs: '30%', sm: 'auto' } }}>
            <Typography 
              variant="h6" 
              fontWeight={700} 
              color="primary.main"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              60% Faster
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Pre-op assessments
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', minWidth: { xs: '30%', sm: 'auto' } }}>
            <Typography 
              variant="h6" 
              fontWeight={700} 
              color="success.main"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              HIPAA Compliant
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Enterprise security
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', minWidth: { xs: '30%', sm: 'auto' } }}>
            <Typography 
              variant="h6" 
              fontWeight={700} 
              color="secondary.main"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              24/7 Support
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Expert assistance
            </Typography>
          </Box>
        </Box>
      </Container>
      
      {/* Form Section with Modern Styling */}
      <Container maxWidth="md" sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5, md: 4 },
            borderRadius: 3,
            background: 'white',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Stepper with Professional Styling */}
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: { xs: 2, sm: 3 },
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'primary.main',
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'primary.main',
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: 'text.primary',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: 'text.primary',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '& .MuiStepIcon': {
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {/* Form Content */}
          <Box sx={{ mt: 3, mb: 2 }}>
            {renderStepContent(activeStep)}
          </Box>
        
          <Divider sx={{ my: 2 }} />
          
          {/* Navigation Buttons with Modern Styling */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              size="medium"
              sx={{ 
                mr: 1,
                color: '#1a1a1a',
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="outlined"
                  onClick={handleSubmit}
                  disabled={loading}
                  size="medium"
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                  sx={{
                    borderColor: '#1a1a1a',
                    color: '#1a1a1a',
                    px: 3,
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#1a1a1a',
                      bgcolor: '#1a1a1a',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      borderWidth: 2,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  Submit Request
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size="medium"
                  sx={{
                    bgcolor: '#0066FF',
                    color: 'white',
                    px: 3,
                    py: 1,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#0052cc',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0, 102, 255, 0.25)',
                    },
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
      {/* Minimal Footer */}
      <Box
        sx={{
          py: 3,
          px: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Questions? Call <strong>(888) 123-4567</strong>
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Email <strong>sales@virtualpat.ai</strong>
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={8000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            fontSize: '1rem',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              fontSize: '2rem',
            },
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Thank you for your interest in VirtualPAT!
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              We'll contact you within 24 hours to schedule your personalized demo.
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
      
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
